const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const CHECKOUT_ATTEMPT_STATES = [
    'prepared',
    'started',
    'form_ready',
    'completed',
    'expired',
    'cancel_returned',
    'superseded',
] as const;

export const CHECKOUT_TRIAL_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'canceled',
    'unpaid',
    'incomplete',
    'incomplete_expired',
    'paused',
] as const;

export type CheckoutAttemptState = typeof CHECKOUT_ATTEMPT_STATES[number];
export type CheckoutTrialStatus = typeof CHECKOUT_TRIAL_STATUSES[number];

export type CheckoutAttemptResult = {
    attemptId: string;
    state: CheckoutAttemptState;
    completed: boolean;
    checkoutSessionId: string | null;
    subscriptionId: string | null;
    stripeSubscriptionId: string | null;
    organizationId: string;
    projectId: string | null;
    resultDigestId: string | null;
    planLookupKey: string | null;
    trialStatus: CheckoutTrialStatus | null;
    destination: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requiredUuid(value: unknown, field: string): string {
    if (typeof value !== 'string' || !UUID_PATTERN.test(value)) {
        throw new Error(`Checkout result returned invalid ${field}.`);
    }
    return value.toLowerCase();
}

export function parsePreparedCheckoutAttemptId(value: unknown): string {
    return requiredUuid(value, 'prepared attempt ID');
}

function nullableUuid(value: unknown, field: string): string | null {
    if (value === null) return null;
    return requiredUuid(value, field);
}

function nullableNonEmptyString(value: unknown, field: string): string | null {
    if (value === null) return null;
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new Error(`Checkout result returned invalid ${field}.`);
    }
    return value.trim();
}

function safeDestination(value: unknown): string | null {
    const destination = nullableNonEmptyString(value, 'destination');
    if (destination === null) return null;
    if (!destination.startsWith('/') || destination.startsWith('//') || destination.includes('\\')) {
        throw new Error('Checkout result returned an unsafe destination.');
    }

    const parsed = new URL(destination, 'https://paperzilla.ai');
    if (parsed.origin !== 'https://paperzilla.ai') {
        throw new Error('Checkout result returned an unsafe destination.');
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function parseCheckoutAttemptResult(value: unknown): CheckoutAttemptResult {
    if (!isRecord(value)) {
        throw new Error('Checkout result was not an object.');
    }

    const state = value.state;
    if (
        typeof state !== 'string'
        || !(CHECKOUT_ATTEMPT_STATES as readonly string[]).includes(state)
    ) {
        throw new Error('Checkout result returned an unknown state.');
    }

    const trialStatus = value.trial_status;
    if (
        trialStatus !== null
        && (
            typeof trialStatus !== 'string'
            || !(CHECKOUT_TRIAL_STATUSES as readonly string[]).includes(trialStatus)
        )
    ) {
        throw new Error('Checkout result returned an unknown trial status.');
    }

    if (typeof value.completed !== 'boolean') {
        throw new Error('Checkout result returned invalid completion state.');
    }

    const projectId = nullableUuid(value.project_id, 'project ID');
    const resultDigestId = nullableUuid(value.result_digest_id, 'result digest ID');
    if (resultDigestId && !projectId) {
        throw new Error('Checkout result returned a digest without its project.');
    }

    return {
        attemptId: requiredUuid(value.attempt_id, 'attempt ID'),
        state: state as CheckoutAttemptState,
        completed: value.completed,
        checkoutSessionId: nullableNonEmptyString(value.checkout_session_id, 'Checkout Session ID'),
        subscriptionId: nullableUuid(value.subscription_id, 'subscription ID'),
        stripeSubscriptionId: nullableNonEmptyString(value.stripe_subscription_id, 'Stripe subscription ID'),
        organizationId: requiredUuid(value.organization_id, 'organization ID'),
        projectId,
        resultDigestId,
        planLookupKey: nullableNonEmptyString(value.plan_lookup_key, 'plan lookup key'),
        trialStatus: trialStatus as CheckoutTrialStatus | null,
        destination: safeDestination(value.destination),
    };
}
